import { useEffect, useState, useRef } from "react";
import {
  getAllParticipants,
  postNewParticipantCertificationDate,
  postNewParticipantCpocDate,
  postNewParticipantCpocEndDate,
  postNewParticipantLinkageDate,
  toggleParticipantDeactivated,
} from "../../../services/participantServices/ParticipantService";
import { useAuth } from "../../../firebase/AuthProvider";
import AssignedParticipant from "../../../interfaces/participant/AssignedParticipant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import Banner from "../../ui/Banner/Banner";
import TransferParticipantComponent from "./TransferParticipantComponent";
import { Button, Form, InputGroup } from "react-bootstrap";
import AccordionComponent from "../../ui/Accordion/AccordionComponent";
import dummyimg from "../../../assets/images/dummyimg.png";
import moment from "moment";
import editicons from "../../../assets/images/editicons.svg";
import backarrowbtn from "../../../assets/images/backarrowbtn.svg";
import ToggleSwitch from "../../ui/Forms/ToggleSwitch";
import ConfirmationModal from "../../ui/ConfirmationModals/ConfirmationModal";
import { toast } from "react-toastify";
import { showConfirmationDialog } from "../../modal/ConfimationModal/Confimation";
import NoParticipantsFound from "../participantfiles/noParticipantsFound";

interface groupedParticipants {
  [assigneeId: string]: AssignedParticipant[];
}

const EditParticipantPage: React.FC = () => {
  const { currentUser, currentRoles } = useAuth();
  const navigate = useNavigate();
  const [reload, setReload] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [displayParticipantInfo, setDisplayParticipantInfo] =
    useState<string>("");
  const [editLinkageDateInfo, setEditLinkageDateInfo] = useState<string>("");
  const [editCertificationDateInfo, setEditCertificationDateInfo] =
    useState<string>("");
  const [editCpocDateInfo, setEditCpocDateInfo] = useState<string>("");
  const [editCpocEndDateInfo, setEditCpocEndDateInfo] = useState<string>("");
  const [linkageDate, setLinkageDate] = useState<string>("");
  const [certificationDate, setCertificationDate] = useState<string>("");
  const [cpocDate, setCpocDate] = useState<string>("");
  const [cpocEndDate, setCpocEndDate] = useState<string>("");
  const [toggleTransferParticipant, setToggleTransferParticipant] =
    useState<boolean>(false);
  const [participants, setParticipants] = useState<
    AssignedParticipant[] | null
  >();
  const [groupedParticipants, setGroupedParticipants] =
    useState<groupedParticipants | null>();

  const [activeAssigneeId, setActiveAssigneeId] = useState<string | null>(null);
  const [activeParticipantId, setActiveParticipantId] = useState<string | null>(
    null
  );
  const [participantToggles, setParticipantToggles] = useState<{
    [id: string]: boolean;
  }>({});
  // const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState<boolean>(false);
  // const [participantToDeactivate, setParticipantToDeactivate] = useState<AssignedParticipant | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const firstLoadRef = useRef<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filteredParticipants, setFilteredParticipants] = useState<
    groupedParticipants | null | undefined
  >(null);
  const [highlightedParticipantId, setHighlightedParticipantId] = useState<
    string | null
  >(null);

  useEffect(() => {
    const fetchParticipants = async () => {
      // show loading spinner only on first load
      if (firstLoadRef.current) {
        setLoading(true);
      }
      if (currentUser !== null) {
        const authToken = await currentUser.getIdToken();
        const fetchedParticipants = await getAllParticipants(authToken, true);
        setParticipants(fetchedParticipants);

        if (fetchedParticipants) {
          const initialToggles = fetchedParticipants.reduce((acc, p) => {
            acc[p.id] = !p.deactivated;
            return acc;
          }, {} as { [id: string]: boolean });
          setParticipantToggles(initialToggles);
        }
      }
      // turn off spinner after first fetch only
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
    };

    fetchParticipants();
  }, [currentUser, reload]);

  // Group participants
  useEffect(() => {
    if (participants) {
      const groupedByAssignee = participants.reduce((group, participant) => {
        const assigneeId = participant.assignee.id;

        if (!group[assigneeId]) {
          group[assigneeId] = [];
        }

        group[assigneeId].push(participant);
        return group;
      }, {} as { [assigneeId: string]: AssignedParticipant[] });

      setGroupedParticipants(groupedByAssignee);
      // Set the first assignee to be active by default
      if (Object.keys(groupedByAssignee).length > 0) {
        setActiveAssigneeId(Object.keys(groupedByAssignee)[0]);
      }
    }
  }, [participants]);

  useEffect(() => {
    if (searchQuery === "") {
      setFilteredParticipants(groupedParticipants);
      setHighlightedParticipantId(null);
      return;
    }

    if (groupedParticipants) {
      const filtered = Object.keys(groupedParticipants).reduce(
        (acc, assigneeId) => {
          const matchingParticipants = groupedParticipants[assigneeId].filter(
            (p) =>
              `${p.firstName} ${p.lastName}`
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
          );
          if (matchingParticipants.length > 0) {
            acc[assigneeId] = matchingParticipants;
          }
          return acc;
        },
        {} as groupedParticipants
      );

      setFilteredParticipants(filtered);

      const firstMatch = Object.values(filtered)[0]?.[0];
      if (firstMatch) {
        setHighlightedParticipantId(firstMatch.id);
        setActiveAssigneeId(firstMatch.assignee.id);
        setActiveParticipantId(firstMatch.id);
      } else {
        setHighlightedParticipantId(null);
      }
    }
  }, [searchQuery, groupedParticipants]);

  const handleToggleDeactivated = async (id: string) => {
    if (currentUser !== undefined && currentUser) {
      try {
        const authToken: string = await currentUser.getIdToken();
        const result = await toggleParticipantDeactivated(id, authToken);
        if (result.success) {
          setParticipantToggles((prev) => ({ ...prev, [id]: !prev[id] }));
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
        // No full reload, just update the toggle state
      } catch (err) {
        setErrorMessage("Failed to update participant status.");
        // Revert toggle on failure
        setParticipantToggles((prev) => ({ ...prev, [id]: !prev[id] }));
      }
    }
  };

  const promptDeactivation = async (participant: AssignedParticipant) => {
    if (participantToggles[participant.id]) {
      const isConfirm = await showConfirmationDialog(
        `Are you sure you want to deactivate this user?`,
        `User deactivation will revoke system access and remove this user from all task views, filters, and assignment options.`
      );
      if (!isConfirm) return;
      // setParticipantToDeactivate(participant);
      // setIsDeactivateModalOpen(true);
      handleToggleDeactivated(participant.id);
    } else {
      handleToggleDeactivated(participant.id);
    }
  };

  const onChangeLinkageDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setLinkageDate(newDate);
  };

  const onChangeCpocDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setCpocDate(newDate);
  };

  const onChangeCpocEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setCpocEndDate(newDate);
  };

  const onChangeCertificationDate = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newDate = event.target.value;
    setCertificationDate(newDate);
  };

  const navigateCreateParticipant = () => {
    navigate("new");
  };

  const clearDisplayInfo = () => {
    setDisplayParticipantInfo("");
    setEditCertificationDateInfo("");
    setEditCpocDateInfo("");
    setEditCpocEndDateInfo("");
    setEditLinkageDateInfo("");
    setLinkageDate("");
    setCertificationDate("");
    setCpocDate("");
    setCpocEndDate("");
  };

  const toggleEditLinkageDateInfo = (id: string) => {
    if (editLinkageDateInfo === id) {
      setEditLinkageDateInfo("");
    } else {
      setEditLinkageDateInfo(id);
    }
  };

  const toggleEditCertificationDateInfo = (id: string) => {
    if (editCertificationDateInfo === id) {
      setEditCertificationDateInfo("");
    } else {
      setEditCertificationDateInfo(id);
    }
  };

  const toggleEditCpocDateInfo = (id: string) => {
    if (editCpocDateInfo === id) {
      setEditCpocDateInfo("");
    } else {
      setEditCpocDateInfo(id);
    }
  };

  const toggleEditCpocEndDateInfo = (id: string) => {
    if (editCpocEndDateInfo === id) {
      setEditCpocEndDateInfo("");
    } else {
      setEditCpocEndDateInfo(id);
    }
  };

  const validateLinkageDate = () => {
    const isValid = !isNaN(Date.parse(linkageDate));
    return isValid;
  };

  const validateCertificationDate = () => {
    const isValid = !isNaN(Date.parse(certificationDate));
    return isValid;
  };

  const validateCpocDate = () => {
    const isValid = !isNaN(Date.parse(cpocDate));
    return isValid;
  };

  const validateCpocEndDate = () => {
    const isValid = !isNaN(Date.parse(cpocEndDate));
    return isValid;
  };

  const postNewLinkageDate = async (id: string) => {
    try {
      if (validateLinkageDate() && currentUser) {
        const authToken: string = await currentUser.getIdToken();
        await postNewParticipantLinkageDate(
          id,
          new Date(linkageDate),
          authToken
        );
        setErrorMessage("");
        toggleEditLinkageDateInfo(id);
        clearDisplayInfo();
        setReload(!reload);
      } else if (!validateLinkageDate()) {
        setErrorMessage("Linkage Date is not a valid date format.");
      }
    } catch {
      setErrorMessage("Unable to update linkage date");
    }
  };

  const postNewCertificationDate = async (id: string) => {
    try {
      if (validateCertificationDate() && currentUser) {
        const authToken: string = await currentUser.getIdToken();
        await postNewParticipantCertificationDate(
          id,
          new Date(certificationDate),
          authToken
        );
        setErrorMessage("");
        toggleEditCertificationDateInfo(id);
        clearDisplayInfo();
        setReload(!reload);
      } else if (!validateCertificationDate()) {
        setErrorMessage("Certification Date is not a valid date format.");
      }
    } catch {
      setErrorMessage("Unable to update certification date");
    }
  };

  const postNewCpocDate = async (id: string) => {
    try {
      if (validateCpocDate() && currentUser) {
        const authToken: string = await currentUser.getIdToken();
        await postNewParticipantCpocDate(id, new Date(cpocDate), authToken);
        setErrorMessage("");
        toggleEditCpocDateInfo(id);
        clearDisplayInfo();
        setReload(!reload);
      } else if (!validateCpocDate()) {
        setErrorMessage("CPOC Date is not a valid date format.");
      }
    } catch {
      setErrorMessage("Unable to update CPOC date");
    }
  };

  const postNewCpocEndDate = async (id: string) => {
    try {
      if (validateCpocEndDate() && currentUser) {
        const authToken: string = await currentUser.getIdToken();
        await postNewParticipantCpocEndDate(
          id,
          new Date(cpocEndDate),
          authToken
        );
        setErrorMessage("");
        toggleEditCpocEndDateInfo(id);
        clearDisplayInfo();
        setReload(!reload);
      } else if (!validateCpocEndDate()) {
        setErrorMessage("CPOC Date is not a valid date format.");
      }
    } catch {
      setErrorMessage("Unable to update CPOC date");
    }
  };

  const dismissBanner = () => {
    setErrorMessage("");
  };

  const backBtnUrl = (): string => {
    if (
      currentRoles?.some(
        (role) => role.name === "OWNER" || role.name === "ADMIN"
      )
    ) {
      return "/dashboard/owner/settings";
    } else {
      return "/settings";
    }
  };

  const getHighlightedText = (text: string, highlight: string) => {
    if (!highlight.trim()) {
      return <span>{text}</span>;
    }
    const parts = text.split(new RegExp(`(${highlight})`, "gi"));
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <span
              key={i}
              style={{ backgroundColor: "rgba(126, 87, 194, 0.3)" }}
            >
              {part}
            </span>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  return (
    <div className="relative">
      <div className="common_card relative">
        <div className="top top_bar_flx">
          <div className="top_bar_tittle">
            <h2>
              <Link to={backBtnUrl()}>
                <img style={{ width: "1.5rem" }} src={backarrowbtn} alt="" />
              </Link>
              Participants
            </h2>
          </div>
          <div className="top_right_flx flex-wrap justify-content-end">
            <Button
              className="btn_sm"
              variant="secondary"
              onClick={() =>
                setToggleTransferParticipant(!toggleTransferParticipant)
              }
            >
              Transfer Participant
            </Button>
            <Button
              className="btn_sm"
              variant="primary"
              onClick={navigateCreateParticipant}
            >
              New Participant
            </Button>
          </div>
        </div>
        <Form.Control
          type="text"
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="btn_sm max-w-[400px]"
        />
        {errorMessage !== "" && (
          <div className="my-5">
            <Banner
              status={"error"}
              message={errorMessage}
              dismissBanner={dismissBanner}
            />
          </div>
        )}
        {toggleTransferParticipant && (
          <TransferParticipantComponent
            participants={participants}
            isOpen={toggleTransferParticipant}
            onClose={() => setToggleTransferParticipant(false)}
          />
        )}
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <span>Loading participants...</span>
          </div>
        ) : !participants || participants.length === 0 ? (
          <div className="flex justify-center items-center py-10">
            <div className="no-participants-found-container">
                <NoParticipantsFound />
              </div>
          </div>
        ) : (
          <div className="my_accordian participants_accordian space-y-2">
            {filteredParticipants &&
              Object.keys(groupedParticipants!).map((assigneeId, index) => {
                const isGroupInFilteredResults =
                  !!filteredParticipants[assigneeId];
                const assignee = groupedParticipants![assigneeId][0].assignee;

                return (
                  <div
                    key={assigneeId}
                    className={`${
                      !isGroupInFilteredResults && searchQuery
                        ? "opacity-50"
                        : ""
                    }`}
                  >
                    <AccordionComponent
                      isOpen={activeAssigneeId === assigneeId}
                      onToggle={() =>
                        setActiveAssigneeId(
                          activeAssigneeId === assigneeId ? null : assigneeId
                        )
                      }
                      title={
                        <>
                          {assignee.firstName} {assignee.lastName}{" "}
                          <span>
                            (
                            {
                              groupedParticipants![assigneeId].filter(
                                (participant) => !participant.deactivated
                              ).length
                            }{" "}
                            participants)
                          </span>
                        </>
                      }
                    >
                      {isGroupInFilteredResults &&
                        filteredParticipants[assigneeId]
                          .sort((a, b) => a.lastName.localeCompare(b.lastName))
                          .map((participant, i) => {
                            const isHighlighted =
                              highlightedParticipantId === participant.id;
                            const participantRef = isHighlighted
                              ? (el: HTMLDivElement) => {
                                  el?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                  });
                                }
                              : null;

                            return (
                              <div
                                ref={participantRef}
                                key={participant.id}
                                className={`${
                                  isHighlighted ? "bg-yellow-100" : ""
                                }`}
                              >
                                <AccordionComponent
                                  isOpen={
                                    activeParticipantId === participant.id
                                  }
                                  onToggle={() =>
                                    setActiveParticipantId(
                                      activeParticipantId === participant.id
                                        ? null
                                        : participant.id
                                    )
                                  }
                                  title={
                                    <div className="participants_accordian_profile">
                                      <div className="participants_accordian_profile_img shrink-0">
                                        <img src={dummyimg} alt="dummyimg" />
                                      </div>
                                      <div className="participants_accordian_profile_txt">
                                        <div className="flex items-center align-middle gap-2">
                                          <h3>
                                            {" "}
                                            {getHighlightedText(
                                              `${participant.firstName} ${participant.lastName}`,
                                              searchQuery
                                            )}
                                          </h3>
                                          &#183;
                                          <span className="text-muted font-normal italic text-xs">
                                            Joined:{" "}
                                            {moment(
                                              participant.creationDate
                                            ).format("MM/DD/YYYY")}
                                          </span>
                                        </div>
                                        <p>
                                          Region {participant.region} -{" "}
                                          {participant.waiver.waiverName}
                                        </p>
                                      </div>
                                      <div className="m-3">
                                        <ToggleSwitch
                                          id={`participant-toggle-${participant.id}`}
                                          checked={
                                            participantToggles[
                                              participant.id
                                            ] || false
                                          }
                                          onChange={() =>
                                            promptDeactivation(participant)
                                          }
                                          label="Active: "
                                        />
                                      </div>
                                    </div>
                                  }
                                >
                                  <div className="participants_accordian_form_grid">
                                    <div className="participants_accordian_form_grid_items">
                                      <Form.Group
                                        className="custom_input_flled mb-0 w-auto"
                                        controlId=""
                                      >
                                        <Form.Label className="mb-0">
                                          Linkage Date
                                        </Form.Label>
                                        <InputGroup
                                          size="sm"
                                          className="ms-2 w-auto"
                                        >
                                          {editLinkageDateInfo !=
                                          participant.id ? (
                                            <>
                                              <Form.Control
                                                type="text"
                                                placeholder="Recipient's username"
                                                aria-label="Recipient's username"
                                                aria-describedby="basic-addon2"
                                                readOnly
                                                value={
                                                  participant.linkageDate
                                                    ? moment(
                                                        participant.linkageDate
                                                      ).format("YYYY-MM-DD")
                                                    : "N/A"
                                                }
                                              />
                                              <Button
                                                id="button-addon2"
                                                className="border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                                onClick={() => {
                                                  toggleEditLinkageDateInfo(
                                                    participant.id
                                                  );
                                                  if (participant.linkageDate) {
                                                    const formatted = moment(
                                                      participant.linkageDate
                                                    ).format("YYYY-MM-DD");
                                                    setLinkageDate(formatted);
                                                  }
                                                }}
                                              >
                                                <img
                                                  src={editicons}
                                                  alt="img"
                                                  className="w-4 h-4"
                                                />
                                              </Button>
                                            </>
                                          ) : (
                                            <>
                                              <Form.Control
                                                type="date"
                                                className="ps-5"
                                                placeholder="Recipient's username"
                                                aria-label="Recipient's username"
                                                aria-describedby="basic-addon2"
                                                value={linkageDate}
                                                onChange={onChangeLinkageDate}
                                              />
                                              <Button
                                                variant="outline-secondary"
                                                id="button-addon2"
                                                onClick={() => {
                                                  postNewLinkageDate(
                                                    participant.id
                                                  );
                                                }}
                                              >
                                                <FontAwesomeIcon
                                                  icon={faCheck}
                                                />
                                              </Button>
                                            </>
                                          )}
                                        </InputGroup>
                                      </Form.Group>
                                    </div>
                                    <div className="participants_accordian_form_grid_items">
                                      <Form.Group
                                        className="custom_input_flled mb-0 w-auto"
                                        controlId=""
                                      >
                                        <Form.Label>CPOC Date</Form.Label>
                                        <InputGroup
                                          size="sm"
                                          className="ms-2 w-auto"
                                        >
                                          {editCpocDateInfo !=
                                          participant.id ? (
                                            <>
                                              <Form.Control
                                                type="text"
                                                placeholder="Recipient's username"
                                                aria-label="Recipient's username"
                                                aria-describedby="basic-addon2"
                                                readOnly
                                                value={
                                                  participant.cpocDate
                                                    ? moment(
                                                        participant.cpocDate
                                                      ).format("YYYY-MM-DD")
                                                    : "N/A"
                                                }
                                              />
                                              <Button
                                                id="button-addon2"
                                                className="border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                                onClick={() => {
                                                  toggleEditCpocDateInfo(
                                                    participant.id
                                                  );
                                                  if (participant.cpocDate) {
                                                    const formatted = moment(
                                                      participant.cpocDate
                                                    ).format("YYYY-MM-DD");
                                                    setCpocDate(formatted);
                                                  }
                                                }}
                                              >
                                                <img
                                                  src={editicons}
                                                  alt="img"
                                                  className="w-4 h-4"
                                                />
                                              </Button>
                                            </>
                                          ) : (
                                            <>
                                              <Form.Control
                                                type="date"
                                                placeholder="Recipient's username"
                                                aria-label="Recipient's username"
                                                aria-describedby="basic-addon2"
                                                className="ps-5"
                                                value={cpocDate}
                                                onChange={onChangeCpocDate}
                                              />
                                              <Button
                                                variant="outline-secondary"
                                                id="button-addon2"
                                                onClick={() => {
                                                  postNewCpocDate(
                                                    participant.id
                                                  );
                                                }}
                                              >
                                                <FontAwesomeIcon
                                                  icon={faCheck}
                                                />
                                              </Button>
                                            </>
                                          )}
                                        </InputGroup>
                                      </Form.Group>
                                    </div>
                                    <div className="participants_accordian_form_grid_items">
                                      <Form.Group
                                        className="custom_input_flled mb-0 w-auto"
                                        controlId=""
                                      >
                                        <Form.Label>CPOC End Date</Form.Label>
                                        <InputGroup
                                          size="sm"
                                          className="ms-2 w-auto"
                                        >
                                          {editCpocEndDateInfo !=
                                          participant.id ? (
                                            <>
                                              <Form.Control
                                                type="text"
                                                placeholder="Recipient's username"
                                                aria-label="Recipient's username"
                                                aria-describedby="basic-addon2"
                                                readOnly
                                                value={
                                                  participant.cpocEndDate
                                                    ? moment(
                                                        participant.cpocEndDate
                                                      ).format("YYYY-MM-DD")
                                                    : "N/A"
                                                }
                                              />
                                              <Button
                                                id="button-addon2"
                                                className="border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                                onClick={() => {
                                                  toggleEditCpocEndDateInfo(
                                                    participant.id
                                                  );
                                                  if (participant.cpocEndDate) {
                                                    const formatted = moment(
                                                      participant.cpocEndDate
                                                    ).format("YYYY-MM-DD");
                                                    setCpocEndDate(formatted);
                                                  }
                                                }}
                                              >
                                                <img
                                                  src={editicons}
                                                  alt="img"
                                                  className="w-4 h-4"
                                                />
                                              </Button>
                                            </>
                                          ) : (
                                            <>
                                              <Form.Control
                                                type="date"
                                                placeholder="Recipient's username"
                                                aria-label="Recipient's username"
                                                aria-describedby="basic-addon2"
                                                className="ps-5"
                                                value={cpocEndDate}
                                                onChange={onChangeCpocEndDate}
                                              />
                                              <Button
                                                variant="outline-secondary"
                                                id="button-addon2"
                                                onClick={() => {
                                                  postNewCpocEndDate(
                                                    participant.id
                                                  );
                                                }}
                                              >
                                                <FontAwesomeIcon
                                                  icon={faCheck}
                                                />
                                              </Button>
                                            </>
                                          )}
                                        </InputGroup>
                                      </Form.Group>
                                    </div>
                                    <div className="participants_accordian_form_grid_items">
                                      <Form.Group
                                        className="custom_input_flled mb-0 w-auto"
                                        controlId=""
                                      >
                                        <Form.Label>
                                          Certification Date
                                        </Form.Label>
                                        <InputGroup
                                          size="sm"
                                          className="ms-2 w-auto"
                                        >
                                          {editCertificationDateInfo !=
                                          participant.id ? (
                                            <>
                                              <Form.Control
                                                type="text"
                                                placeholder="Recipient's username"
                                                aria-label="Recipient's username"
                                                aria-describedby="basic-addon2"
                                                readOnly
                                                value={
                                                  participant.certificationDate
                                                    ? moment(
                                                        participant.certificationDate
                                                      ).format("YYYY-MM-DD")
                                                    : "N/A"
                                                }
                                              />
                                              <Button
                                                id="button-addon2"
                                                className="border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                                onClick={() => {
                                                  toggleEditCertificationDateInfo(
                                                    participant.id
                                                  );
                                                  if (
                                                    participant.certificationDate
                                                  ) {
                                                    const formatted = moment(
                                                      participant.certificationDate
                                                    ).format("YYYY-MM-DD");
                                                    setCertificationDate(
                                                      formatted
                                                    );
                                                  }
                                                }}
                                              >
                                                <img
                                                  src={editicons}
                                                  alt="img"
                                                  className="w-4 h-4"
                                                />
                                              </Button>
                                            </>
                                          ) : (
                                            <>
                                              <Form.Control
                                                type="date"
                                                placeholder="Recipient's username"
                                                aria-label="Recipient's username"
                                                aria-describedby="basic-addon2"
                                                className="ps-5"
                                                value={certificationDate}
                                                onChange={
                                                  onChangeCertificationDate
                                                }
                                              />
                                              <Button
                                                variant="outline-secondary"
                                                id="button-addon2"
                                                onClick={() => {
                                                  postNewCertificationDate(
                                                    participant.id
                                                  );
                                                }}
                                              >
                                                <FontAwesomeIcon
                                                  icon={faCheck}
                                                />
                                              </Button>
                                            </>
                                          )}
                                        </InputGroup>
                                      </Form.Group>
                                    </div>
                                  </div>
                                  <div className="form_btn_flx mby_t_24">
                                    <Button
                                      variant="primary"
                                      onClick={() =>
                                        navigate(
                                          `/edit/participants/bump/${participant.id}`
                                        )
                                      }
                                    >
                                      Waiver Bump
                                    </Button>
                                  </div>
                                </AccordionComponent>
                              </div>
                            );
                          })}
                    </AccordionComponent>
                  </div>
                );
              })}
          </div>
        )}
      </div>
      {/* {isDeactivateModalOpen && participantToDeactivate && (
                <ConfirmationModal
                    isOpen={isDeactivateModalOpen}
                    onClose={() => setIsDeactivateModalOpen(false)}
                    onConfirm={() => {
                        handleToggleDeactivated(participantToDeactivate.id);
                        setIsDeactivateModalOpen(false);
                    }}
                    title="Confirm Deactivation"
                    confirmText="Deactivate"
                >
                    Are you sure you want to deactivate {participantToDeactivate.firstName} {participantToDeactivate.lastName}?
                </ConfirmationModal>
            )} */}
    </div>
  );
};

export default EditParticipantPage;
